import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  CartesianViewportHandle,
  FigureElement,
  FigureHandle,
  FormulaLegendHandle,
  ReadoutHandle,
  ReadoutRow,
} from '@learnforge/demo-render';
import type { GridHandle } from '@learnforge/demo-render';
import { createCartesianViewport, createFormulaLegend, createSvgEl } from '@learnforge/demo-render';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import { parse, derivative, simplify, stringify } from '@learnforge/demo-math/expr';
import { clamp, formatNumber } from '@learnforge/demo-math/numeric';
import type { CalculusContext } from '../types';
import type { CalcKey } from '../locales/keys';
import { createCurveLayer, type CurveLayerHandle } from '../render/curve';
import { compileFn, sampleFn, criticalPoints, extremaFromCritical } from '../calc';
import { APPLICATION_PRESETS, type ApplicationPreset } from '../data/application-presets';
import {
  createCartesianPlane,
  isPointInCartesianDomain,
  type CartesianPlaneHandle,
} from '../render/cartesian-plane';

// ─── Stage layout constants ───────────────────────────────────────────────────

const UNIT = 50;
const STAGE_W = 800;
const STAGE_H = 460;

/** Top scenario pane: occupies the upper 55% of the stage (0..253px). */
const SCENARIO_H = 250;
/** Bottom quantity-curve pane: occupies the lower 45% (260..460px). */
const QUANTITY_TOP = 260;
const QUANTITY_H = STAGE_H - QUANTITY_TOP; // 200px
const QUANTITY_RECT = { x: 40, y: QUANTITY_TOP + 10, width: STAGE_W - 80, height: QUANTITY_H - 20 };

/** Number of sample points per curve. */
const SAMPLE_N = 300;

/** Arrow-head half-width and depth in screen pixels. */
const ARROWHEAD_SIZE = 7;

// ─── State ────────────────────────────────────────────────────────────────────

export interface ApplicationsState {
  presetId: string;
  /** Current parameter value (x); drives both the scenario geometry and the marker. */
  param: number;
  /** Active mode — optimization shows the curve+marker; related-rates shows rate vectors. */
  mode: 'optimization' | 'related-rates';
}

// ─── Exported pure helpers (unit-tested) ─────────────────────────────────────

export interface OptimumResult {
  x: number;
  y: number;
  kind: 'min' | 'max' | 'inflection';
}

/**
 * Find the interior optimum of `quantitySource` over `domain`.
 *
 * Builds f′ symbolically, finds critical points numerically, classifies them,
 * and returns the extremum with the largest |y| (the "principal" optimum — the
 * box-volume and rectangle-area presets both have a single interior maximum).
 *
 * If no interior critical point is found, falls back to a numerical maximum
 * over the domain (edge case: flat or monotone functions).
 */
export function optimumOf(quantitySource: string, domain: [number, number]): OptimumResult {
  const fNode = parse(quantitySource).node;
  const fPrimeNode = simplify(derivative(fNode, 'x'));
  const fDoublePrimeNode = simplify(derivative(fPrimeNode, 'x'));

  const f = compileFn(quantitySource);
  const fPrime = compileFn(stringify(fPrimeNode));
  const fDoublePrime = compileFn(stringify(fDoublePrimeNode));

  const [x0, x1] = domain;
  const crits = criticalPoints(fPrime, x0, x1, { steps: 600 });

  if (crits.length === 0) {
    // No interior critical point — scan and return the point with max |y|.
    const pts = sampleFn(f, x0, x1, 200);
    let best = pts[0] ?? { x: x0, y: f(x0) };
    for (const p of pts) {
      if (Number.isFinite(p.y) && Math.abs(p.y) > Math.abs(best.y)) {
        best = p;
      }
    }
    return { x: best.x, y: best.y, kind: 'max' };
  }

  const extrema = extremaFromCritical(f, fPrime, fDoublePrime, crits);

  // Pick the most prominent extremum (max |y|).
  let best = extrema[0]!;
  for (const e of extrema) {
    if (Math.abs(e.y) > Math.abs(best.y)) {
      best = e;
    }
  }

  return { x: best.x, y: best.y, kind: best.kind };
}

// ─── Related-rates helpers ────────────────────────────────────────────────────

export interface LadderParams {
  /** Current base length (x). */
  x: number;
  /** Fixed ladder length. */
  length: number;
  /** Rate of change of the base (dx/dt). */
  dxdt: number;
}

export interface RippleParams {
  /** Current radius (r). */
  r: number;
  /** Rate of change of the radius (dr/dt). */
  drdt: number;
}

export interface RelatedRatesResult {
  vectors: Record<string, number>;
  equation: string;
}

/**
 * Compute related-rates quantities for the `ladder` and `ripple` scenarios.
 *
 * Ladder: x² + y² = L²  ⟹  dy/dt = −(x/y) · dx/dt
 * Ripple: A = πr²  ⟹  dA/dt = 2πr · dr/dt
 */
export function relatedRates(scenario: 'ladder', params: LadderParams): RelatedRatesResult;
export function relatedRates(scenario: 'ripple', params: RippleParams): RelatedRatesResult;
export function relatedRates(
  scenario: 'ladder' | 'ripple',
  params: LadderParams | RippleParams,
): RelatedRatesResult {
  if (scenario === 'ladder') {
    const { x, length, dxdt } = params as LadderParams;
    const y = Math.sqrt(Math.max(0, length * length - x * x));
    const dydt = y > 1e-10 ? -(x / y) * dxdt : 0;
    return {
      vectors: { dxdt, dydt },
      equation: 'dy/dt = −(x/y) · dx/dt',
    };
  } else {
    const { r, drdt } = params as RippleParams;
    const dAdt = 2 * Math.PI * r * drdt;
    return {
      vectors: { drdt, dAdt },
      equation: 'dA/dt = 2πr · dr/dt',
    };
  }
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

/** Build a small arrowhead polygon at `tip` pointing in direction `dir` (normalised). */
function arrowheadPoints(tip: Vec2, dir: Vec2): string {
  // Perpendicular unit vector
  const px = -dir.y;
  const py = dir.x;
  const depth = ARROWHEAD_SIZE;
  const halfW = ARROWHEAD_SIZE * 0.5;
  // Base of the arrowhead, offset from tip by depth along -dir
  const bx = tip.x - dir.x * depth;
  const by = tip.y - dir.y * depth;
  return [
    `${tip.x.toFixed(1)},${tip.y.toFixed(1)}`,
    `${(bx + px * halfW).toFixed(1)},${(by + py * halfW).toFixed(1)}`,
    `${(bx - px * halfW).toFixed(1)},${(by - py * halfW).toFixed(1)}`,
  ].join(' ');
}

/** Sample a quantity function and return [min, max] of finite y. */
function quantityYRange(
  f: (x: number) => number,
  domain: [number, number],
  n: number,
): [number, number] {
  const pts = sampleFn(f, domain[0], domain[1], n);
  let yMin = Infinity;
  let yMax = -Infinity;
  for (const p of pts) {
    if (Number.isFinite(p.y)) {
      if (p.y < yMin) yMin = p.y;
      if (p.y > yMax) yMax = p.y;
    }
  }
  if (!Number.isFinite(yMin) || !Number.isFinite(yMax)) return [0, 1];
  const pad = (yMax - yMin) * 0.15 + 0.5;
  return [Math.min(0, yMin - pad), yMax + pad];
}

function compactNumber(value: number): string {
  return formatNumber(value, { decimals: 3 }).replace(/(?:\.0+|(\.\d*?)0+)$/, '$1');
}

function tangentFormula(at: number, value: number, slope: number): string {
  const displaySlope = Math.abs(slope) < 0.0005 ? 0 : slope;
  const slopeSign = displaySlope < 0 ? '−' : '+';
  const centerSign = at < 0 ? '+' : '−';
  return `T(x) ≈ ${compactNumber(value)} ${slopeSign} ${compactNumber(Math.abs(displaySlope))}(x ${centerSign} ${compactNumber(Math.abs(at))})`;
}

// ─── Scenario-specific rendering helpers ─────────────────────────────────────

/**
 * Build figure elements for the open-box scenario.
 * The box is formed by cutting a square of side `x` from each corner of a
 * 10×10 sheet and folding up the sides. We render a simple flat net view:
 * a rectangle of width (10−2x) with side-flap indicators.
 */
function buildOpenBoxElements(param: number, grid: GridHandle): FigureElement[] {
  // Sheet size is 10×10 world units; UNIT px/world. Render centered in the
  // scenario pane using the grid's screen coordinate system.
  const sheetHalf = 5; // half the 10-unit sheet, in world units
  const x = clamp(param, 0.1, 4.9);
  const boxW = 10 - 2 * x; // base width
  const boxH = x; // fold-up height

  // We render in a coordinate system where the centre of the scenario
  // pane is at (400, 125) in screen pixels. Map world units manually.
  const cx = STAGE_W / 2;
  const cy = SCENARIO_H / 2 - 10;
  const scale = Math.min((STAGE_W - 80) / 2 / sheetHalf, (SCENARIO_H - 40) / 2 / sheetHalf);

  const wScreen = boxW * scale;
  const xScreen = x * scale;

  // Base rectangle (fold-down box base)
  const bL = cx - wScreen / 2;
  const bR = cx + wScreen / 2;
  const bT = cy - (boxH / 2) * scale;
  const bB = cy + (boxH / 2) * scale;

  // Main box base — show as a filled rectangle drawn with figure segments
  const elements: FigureElement[] = [
    // Base rectangle
    {
      kind: 'point',
      id: 'box-tl',
      at: { x: bL, y: bT },
      style: { className: 'geo-point-off-stage' },
    },
    {
      kind: 'point',
      id: 'box-tr',
      at: { x: bR, y: bT },
      style: { className: 'geo-point-off-stage' },
    },
    {
      kind: 'point',
      id: 'box-br',
      at: { x: bR, y: bB },
      style: { className: 'geo-point-off-stage' },
    },
    {
      kind: 'point',
      id: 'box-bl',
      at: { x: bL, y: bB },
      style: { className: 'geo-point-off-stage' },
    },
    {
      kind: 'segment',
      id: 'box-top',
      from: 'box-tl',
      to: 'box-tr',
      style: { className: 'geo-line' },
    },
    {
      kind: 'segment',
      id: 'box-right',
      from: 'box-tr',
      to: 'box-br',
      style: { className: 'geo-line' },
    },
    {
      kind: 'segment',
      id: 'box-bottom',
      from: 'box-br',
      to: 'box-bl',
      style: { className: 'geo-line' },
    },
    {
      kind: 'segment',
      id: 'box-left',
      from: 'box-bl',
      to: 'box-tl',
      style: { className: 'geo-line' },
    },
    // Corner flaps (left side)
    {
      kind: 'point',
      id: 'flap-ll-out',
      at: { x: bL - xScreen, y: bT },
      style: { className: 'geo-point-off-stage' },
    },
    {
      kind: 'point',
      id: 'flap-lb-out',
      at: { x: bL - xScreen, y: bB },
      style: { className: 'geo-point-off-stage' },
    },
    {
      kind: 'segment',
      id: 'flap-left-top',
      from: 'flap-ll-out',
      to: 'box-tl',
      style: { className: 'geo-line geo-line--faint', dashed: true },
    },
    {
      kind: 'segment',
      id: 'flap-left-bot',
      from: 'flap-lb-out',
      to: 'box-bl',
      style: { className: 'geo-line geo-line--faint', dashed: true },
    },
    {
      kind: 'segment',
      id: 'flap-left-edge',
      from: 'flap-ll-out',
      to: 'flap-lb-out',
      style: { className: 'geo-line geo-line--faint', dashed: true },
    },
    // Corner flaps (right side)
    {
      kind: 'point',
      id: 'flap-rl-out',
      at: { x: bR + xScreen, y: bT },
      style: { className: 'geo-point-off-stage' },
    },
    {
      kind: 'point',
      id: 'flap-rb-out',
      at: { x: bR + xScreen, y: bB },
      style: { className: 'geo-point-off-stage' },
    },
    {
      kind: 'segment',
      id: 'flap-right-top',
      from: 'box-tr',
      to: 'flap-rl-out',
      style: { className: 'geo-line geo-line--faint', dashed: true },
    },
    {
      kind: 'segment',
      id: 'flap-right-bot',
      from: 'box-br',
      to: 'flap-rb-out',
      style: { className: 'geo-line geo-line--faint', dashed: true },
    },
    {
      kind: 'segment',
      id: 'flap-right-edge',
      from: 'flap-rl-out',
      to: 'flap-rb-out',
      style: { className: 'geo-line geo-line--faint', dashed: true },
    },
    // Draggable param point at the bottom of the box at x
    {
      kind: 'point',
      id: 'pt-param',
      at: grid.worldToScreen({ x: x, y: 0 }),
      style: { className: 'geo-point-draggable' },
      draggable: {},
    },
  ];

  return elements;
}

/**
 * Build figure elements for the rectangle-under-line scenario.
 * The line is y = 6 − 2x; the rectangle has one corner at (x, 0) and the
 * opposite corner at (x, 6−2x). We show the line, the rectangle, and the
 * height/width labels as geometric primitives.
 */
function buildRectElements(param: number, grid: GridHandle): FigureElement[] {
  const x = clamp(param, 0.05, 2.95);
  const lineY = 6 - 2 * x;

  // Use world coordinates directly (UNIT = 50px per unit).
  // Origin is at screen (STAGE_W/2 - MATH_ORIGIN_X*UNIT, STAGE_H/2 + MATH_ORIGIN_Y*UNIT).
  // We will use grid.worldToScreen for placement.
  // World origin (0,0) in math → world (MATH_ORIGIN_X, MATH_ORIGIN_Y) for grid.
  // This experiment uses a single grid, so map math coords to world coords.
  const MATH_OX = STAGE_W / 2 / UNIT; // 8
  const MATH_OY = SCENARIO_H / 2 / UNIT; // ~2.5

  const mathToWorld = (mx: number, my: number): Vec2 => ({
    x: mx + MATH_OX,
    y: MATH_OY - my,
  });

  const origin = grid.worldToScreen(mathToWorld(0, 0));
  const pX0 = grid.worldToScreen(mathToWorld(0, 0));
  const pX1 = grid.worldToScreen(mathToWorld(x, 0));
  const pTop = grid.worldToScreen(mathToWorld(x, lineY));
  const pTopLeft = grid.worldToScreen(mathToWorld(0, lineY));
  const pLineEnd = grid.worldToScreen(mathToWorld(3, 0));
  const pLine0 = grid.worldToScreen(mathToWorld(0, 6));

  const elements: FigureElement[] = [
    // Axes origin helper
    { kind: 'point', id: 'rect-origin', at: origin, style: { className: 'geo-point-off-stage' } },
    // Line y = 6 - 2x from x=0 to x=3 in world
    { kind: 'point', id: 'line-start', at: pLine0, style: { className: 'geo-point-off-stage' } },
    { kind: 'point', id: 'line-end', at: pLineEnd, style: { className: 'geo-point-off-stage' } },
    {
      kind: 'segment',
      id: 'constraint-line',
      from: 'line-start',
      to: 'line-end',
      style: { className: 'geo-line' },
    },
    // Rectangle corners
    { kind: 'point', id: 'rect-bl', at: pX0, style: { className: 'geo-point-off-stage' } },
    { kind: 'point', id: 'rect-br', at: pX1, style: { className: 'geo-point-off-stage' } },
    { kind: 'point', id: 'rect-tr', at: pTop, style: { className: 'geo-point-secondary' } },
    { kind: 'point', id: 'rect-tl', at: pTopLeft, style: { className: 'geo-point-off-stage' } },
    {
      kind: 'segment',
      id: 'rect-bottom',
      from: 'rect-bl',
      to: 'rect-br',
      style: { className: 'geo-line' },
    },
    {
      kind: 'segment',
      id: 'rect-right',
      from: 'rect-br',
      to: 'rect-tr',
      style: { className: 'geo-line' },
    },
    {
      kind: 'segment',
      id: 'rect-top',
      from: 'rect-tr',
      to: 'rect-tl',
      style: { className: 'geo-line geo-line--faint', dashed: true },
    },
    {
      kind: 'segment',
      id: 'rect-left',
      from: 'rect-tl',
      to: 'rect-bl',
      style: { className: 'geo-line geo-line--faint', dashed: true },
    },
    // Draggable param point
    {
      kind: 'point',
      id: 'pt-param',
      at: pX1,
      style: { className: 'geo-point-draggable' },
      draggable: {},
    },
  ];
  return elements;
}

/**
 * Build figure elements for the ladder scenario.
 * Shows a wall (vertical), floor (horizontal), a ladder (hypotenuse), and two
 * arrowhead segments for dx/dt (base) and dy/dt (wall contact point).
 *
 * Arrow geometry:
 *   dx/dt — horizontal arrow at the ladder BASE along the floor, pointing in
 *            the direction the base is sliding (positive → right).
 *   dy/dt — vertical arrow at the TOP of the ladder (wall-contact point / apex),
 *            pointing downward (dy/dt is always negative as the base moves out).
 *
 * Scale formula: scale = min(usableW / L, usableH / L)
 *   usableW = STAGE_W - leftMargin - rightMargin  (right margin reserves arrow space)
 *   usableH = SCENARIO_H - topMargin - bottomMargin
 * This ensures neither the wall height (up to L) nor the base run (up to L) overflows
 * the scenario pane for any parameter value in [0, L].
 */
function buildLadderElements(
  param: number,
  _grid: GridHandle,
  _svg: SVGSVGElement,
  arrowGroup: SVGGElement,
): FigureElement[] {
  const ladderLength = 10;
  const x = clamp(param, 0.5, ladderLength - 0.5);
  const y = Math.sqrt(Math.max(0, ladderLength * ladderLength - x * x));

  // Layout constants for the scenario pane.
  const MARGIN_LEFT = 80; // px: space left of wall for the dy/dt arrow label
  const MARGIN_RIGHT = 80; // px: space right of the base point for the dx/dt arrow
  const MARGIN_TOP = 16; // px: clearance above the highest wall-contact point
  const MARGIN_BOTTOM = 24; // px: clearance below the floor

  // Choose scale so the FULL ladder triangle (wall up to L, base out to L) fits
  // within the usable area for any value of x ∈ [0, L].
  const usableW = STAGE_W - MARGIN_LEFT - MARGIN_RIGHT;
  const usableH = SCENARIO_H - MARGIN_TOP - MARGIN_BOTTOM;
  const scale = Math.min(usableW / ladderLength, usableH / ladderLength);

  // Anchor: wall base is at (MARGIN_LEFT, SCENARIO_H - MARGIN_BOTTOM).
  const wallBaseX = MARGIN_LEFT;
  const wallBaseY = SCENARIO_H - MARGIN_BOTTOM;

  const wallTop: Vec2 = { x: wallBaseX, y: wallBaseY - y * scale };
  const wallBot: Vec2 = { x: wallBaseX, y: wallBaseY };
  const floorEnd: Vec2 = { x: wallBaseX + x * scale, y: wallBaseY };

  // Rate values
  const dxdt = 1; // base sliding out at 1 unit/s
  const dydt = y > 1e-10 ? -(x / y) * dxdt : 0;

  // Arrow lengths: readable but capped so they don't exceed available margin space.
  const MAX_ARROW_PX = 50;
  const ARROW_SCALE = 30; // screen px per unit/s, before clamping

  const dxLen = clamp(Math.abs(dxdt) * ARROW_SCALE, 4, MAX_ARROW_PX);
  const dyLen = clamp(Math.abs(dydt) * ARROW_SCALE, 4, MAX_ARROW_PX);

  // dx/dt: horizontal arrow at the base of the ladder along the floor.
  const dxDir: Vec2 = { x: dxdt >= 0 ? 1 : -1, y: 0 };
  const dxTip: Vec2 = { x: floorEnd.x + dxDir.x * dxLen, y: wallBaseY };

  // dy/dt: vertical arrow anchored AT the wall-contact apex (wallTop), pointing DOWN
  // the wall (dy/dt < 0 means y is decreasing → screen-y increases).
  // With the corrected scale the apex is always on-screen; no clamping needed.
  const dyDir: Vec2 = { x: 0, y: 1 }; // downward in screen space
  const dyShaftStart: Vec2 = { x: wallBaseX, y: wallTop.y };
  const dyTip: Vec2 = { x: wallBaseX, y: wallTop.y + dyLen };

  // Clear previous arrowheads
  while (arrowGroup.firstChild) {
    arrowGroup.removeChild(arrowGroup.firstChild);
  }

  // Draw arrowhead polygons for dx/dt
  if (Math.abs(dxdt) > 1e-10) {
    const poly = createSvgEl('polygon', {
      points: arrowheadPoints(dxTip, dxDir),
      class: 'calc-arrow-dx',
    });
    arrowGroup.appendChild(poly);
  }
  // Draw arrowhead polygon for dy/dt (only when wall height > 0).
  // Anchor is the actual wall-contact apex — now guaranteed on-screen.
  if (Math.abs(dydt) > 1e-10 && y > 1e-10) {
    const poly = createSvgEl('polygon', {
      points: arrowheadPoints(dyTip, dyDir),
      class: 'calc-arrow-dy',
    });
    arrowGroup.appendChild(poly);
  }

  const elements: FigureElement[] = [
    // Wall (vertical, from floor up to the wall-contact apex)
    { kind: 'point', id: 'wall-top', at: wallTop, style: { className: 'geo-point-off-stage' } },
    { kind: 'point', id: 'wall-bot', at: wallBot, style: { className: 'geo-point-off-stage' } },
    {
      kind: 'segment',
      id: 'wall',
      from: 'wall-top',
      to: 'wall-bot',
      style: { className: 'geo-line' },
    },
    // Floor (horizontal, from wall base to ladder base)
    { kind: 'point', id: 'floor-end', at: floorEnd, style: { className: 'geo-point-off-stage' } },
    {
      kind: 'segment',
      id: 'floor',
      from: 'wall-bot',
      to: 'floor-end',
      style: { className: 'geo-line' },
    },
    // Ladder (hypotenuse — from wall-contact apex to base on floor)
    {
      kind: 'segment',
      id: 'ladder',
      from: 'wall-top',
      to: 'floor-end',
      style: { className: 'calc-curve-primary geo-line' },
    },
    // dx/dt arrow shaft (horizontal, at the base along the floor)
    { kind: 'point', id: 'dx-tip', at: dxTip, style: { className: 'geo-point-off-stage' } },
    {
      kind: 'segment',
      id: 'dx-arrow',
      from: 'floor-end',
      to: 'dx-tip',
      style: { className: 'geo-line calc-arrow-shaft-dx' },
    },
    // dy/dt arrow shaft (vertical, anchored at the wall-contact apex pointing down)
    {
      kind: 'point',
      id: 'dy-shaft-start',
      at: dyShaftStart,
      style: { className: 'geo-point-off-stage' },
    },
    { kind: 'point', id: 'dy-tip', at: dyTip, style: { className: 'geo-point-off-stage' } },
    {
      kind: 'segment',
      id: 'dy-arrow',
      from: 'dy-shaft-start',
      to: 'dy-tip',
      style: { className: 'geo-line calc-arrow-shaft-dy' },
    },
    // Draggable base point
    {
      kind: 'point',
      id: 'pt-param',
      at: floorEnd,
      style: { className: 'geo-point-draggable' },
      draggable: {},
    },
  ];
  return elements;
}

/**
 * Build figure elements for the ripple scenario.
 * Shows an expanding circle and a dr/dt + dA/dt readout with rate arrows.
 */
function buildRippleElements(
  param: number,
  _grid: GridHandle,
  _svg: SVGSVGElement,
  arrowGroup: SVGGElement,
): FigureElement[] {
  const r = clamp(param, 0.1, 7.9);
  const drdt = 2; // radius grows at 2 units/s

  // Render circle centred in the scenario pane
  const cx = STAGE_W / 2;
  const cy = SCENARIO_H / 2;
  const maxR = Math.min(cx - 60, cy - 20);
  const rScreen = (r / 8) * maxR;

  // Clear previous arrowheads
  while (arrowGroup.firstChild) {
    arrowGroup.removeChild(arrowGroup.firstChild);
  }

  // Draw dr/dt arrow at the right edge of the circle
  const drTip: Vec2 = { x: cx + rScreen + drdt * 15, y: cy };
  const drDir: Vec2 = { x: 1, y: 0 };
  const drPoly = createSvgEl('polygon', {
    points: arrowheadPoints(drTip, drDir),
    class: 'calc-arrow-dx',
  });
  arrowGroup.appendChild(drPoly);

  // dA/dt is represented by area indicator — show a small upward arrow at top.
  // Compute locally for visual scaling only; the numeric value comes from relatedRates().
  const dAdt = 2 * Math.PI * r * drdt;
  // Cap dAScreen so the arrow tip stays at least 8px inside the pane top.
  const RIPPLE_TOP_MARGIN = 8;
  const dAScreenRaw = Math.sqrt(Math.abs(dAdt)) * 2; // visual scaling
  const dAScreen = Math.min(dAScreenRaw, Math.max(4, cy - rScreen - RIPPLE_TOP_MARGIN));
  const dATip: Vec2 = { x: cx, y: cy - rScreen - dAScreen };
  const dADir: Vec2 = { x: 0, y: -1 };
  const dAPoly = createSvgEl('polygon', {
    points: arrowheadPoints(dATip, dADir),
    class: 'calc-arrow-dy',
  });
  arrowGroup.appendChild(dAPoly);

  const circleEdgeRight: Vec2 = { x: cx + rScreen, y: cy };
  const circleEdgeTop: Vec2 = { x: cx, y: cy - rScreen };

  const elements: FigureElement[] = [
    // Circle centre
    {
      kind: 'point',
      id: 'ripple-centre',
      at: { x: cx, y: cy },
      style: { className: 'geo-point-main' },
    },
    // Circle approximation via arc (use a circle FigureElement)
    {
      kind: 'circle',
      id: 'ripple-circle',
      center: 'ripple-centre',
      through: 'ripple-edge-r',
      style: { className: 'calc-curve-primary' },
    },
    // dr/dt arrow shaft
    { kind: 'point', id: 'dr-tip', at: drTip, style: { className: 'geo-point-off-stage' } },
    {
      kind: 'segment',
      id: 'dr-arrow',
      from: 'ripple-edge-r',
      to: 'dr-tip',
      style: { className: 'geo-line calc-arrow-shaft-dx' },
    },
    // dA/dt arrow shaft
    { kind: 'point', id: 'dA-tip', at: dATip, style: { className: 'geo-point-off-stage' } },
    {
      kind: 'segment',
      id: 'dA-arrow',
      from: 'ripple-edge-t',
      to: 'dA-tip',
      style: { className: 'geo-line calc-arrow-shaft-dy' },
    },
    // Edge reference points for arrow bases
    {
      kind: 'point',
      id: 'ripple-edge-r',
      at: circleEdgeRight,
      style: { className: 'geo-point-off-stage' },
    },
    {
      kind: 'point',
      id: 'ripple-edge-t',
      at: circleEdgeTop,
      style: { className: 'geo-point-off-stage' },
    },
    // Draggable radius handle at the right edge
    {
      kind: 'point',
      id: 'pt-param',
      at: circleEdgeRight,
      style: { className: 'geo-point-draggable' },
      draggable: {},
    },
  ];

  return elements;
}

// ─── Spec factory ─────────────────────────────────────────────────────────────

function buildSpec(): ExperimentSpec<ApplicationsState, CalcKey, CalculusContext> {
  let grid: GridHandle;
  let figure: FigureHandle;
  /** Curve layer for the scenario pane (uses main grid projector — identity pass). */
  let curves: CurveLayerHandle;
  let quantityPlane: CartesianPlaneHandle;
  let quantityPlaneRoot: SVGGElement;
  let quantityCurves: CurveLayerHandle;
  let readout: ReadoutHandle;
  let legend: FormulaLegendHandle;
  let viewport: CartesianViewportHandle | null = null;
  let activeChartKey: string | null = null;
  let stageSvg: SVGSVGElement;
  let requestRender: () => void;

  // SVG groups for overlay elements (arrows, axes) — managed manually.
  let arrowGroup: SVGGElement | null = null;

  // Memoised optimum cache — recomputed only when the preset changes.
  let cachedOptimumPresetId: string | null = null;
  let cachedOptimum: OptimumResult | null = null;

  function defaultState(_ctx: CalculusContext): ApplicationsState {
    const preset =
      APPLICATION_PRESETS.find((p) => p.kind === 'optimization') ?? APPLICATION_PRESETS[0]!;
    const [x0, x1] = preset.domain;
    const mid = (x0 + x1) / 3;
    return {
      presetId: preset.id,
      param: mid,
      mode: preset.kind,
    };
  }

  function applyPresetLocal(state: ApplicationsState, id: string): ApplicationsState {
    const preset = APPLICATION_PRESETS.find((p) => p.id === id);
    if (!preset) return state;
    const [x0, x1] = preset.domain;
    const mid = (x0 + x1) / 3;
    return { ...state, presetId: preset.id, param: mid, mode: preset.kind };
  }

  function getPreset(state: ApplicationsState): ApplicationPreset {
    return APPLICATION_PRESETS.find((p) => p.id === state.presetId) ?? APPLICATION_PRESETS[0]!;
  }

  function clearOverlays(): void {
    if (arrowGroup) {
      arrowGroup.remove();
      arrowGroup = null;
    }
  }

  function chartBase(preset: ApplicationPreset) {
    const quantityFn = compileFn(preset.source!);
    return { xDomain: preset.domain, yDomain: quantityYRange(quantityFn, preset.domain, SAMPLE_N) };
  }

  function setChartMode(preset: ApplicationPreset, enabled: boolean): void {
    if (!enabled) {
      viewport?.dispose();
      viewport = null;
      activeChartKey = null;
      quantityPlaneRoot.style.display = 'none';
      legend.setEntries([]);
      return;
    }

    quantityPlaneRoot.style.display = '';
    const key = `optimization:${preset.id}`;
    if (key === activeChartKey) return;

    viewport?.dispose();
    const base = chartBase(preset);
    quantityPlane.setDomains(base);
    activeChartKey = key;
    viewport = createCartesianViewport(stageSvg, {
      base,
      plotRect: QUANTITY_RECT,
      touchActionTarget: '.calc-cartesian-plane, [data-figure-id="marker"]',
      onChange(next) {
        quantityPlane.setDomains(next);
        requestRender();
      },
    });
  }

  return {
    labelKey: 'experiment.applications',
    demoId: 'calculus',
    advancedKey: 'applications',

    initialState(ctx): ApplicationsState {
      return defaultState(ctx);
    },

    applyPreset(state, id): ApplicationsState {
      return applyPresetLocal(state, id);
    },

    setup(ctx, controller: HarnessController<ApplicationsState>): void {
      stageSvg = controller.svg;
      requestRender = () => controller.requestRender();
      viewport = null;
      activeChartKey = null;
      // Grid covers the full stage; used for axis drawing and figure point placement
      // in the scenario pane. UNIT is deliberately small so the grid doesn't dominate.
      grid = controller.createGrid({ width: STAGE_W, height: STAGE_H, unit: UNIT, faint: true });
      // Scenario-pane curves use the main grid projector (scenario geometry is
      // drawn in screen coords directly, so we won't call setPolyline on this layer
      // for scenario-specific things — it's used only for the tangent lines that
      // the figure can't draw).
      curves = createCurveLayer(controller.svg, grid);
      controller.registerCleanup(() => curves.destroy());
      const initialPreset = APPLICATION_PRESETS.find((preset) => preset.kind === 'optimization')!;
      quantityPlane = createCartesianPlane(controller.svg, {
        domains: chartBase(initialPreset),
        plotRect: QUANTITY_RECT,
      });
      quantityPlaneRoot = quantityPlane.content.closest<SVGGElement>('.calc-cartesian-plane')!;
      controller.registerCleanup(() => quantityPlane.destroy());
      quantityCurves = createCurveLayer(quantityPlane.content, quantityPlane);
      controller.registerCleanup(() => quantityCurves.destroy());
      figure = controller.createFigure();
      readout = controller.createReadout();
      legend = createFormulaLegend(ctx.root);
      controller.registerCleanup(() => legend.destroy());
      controller.registerCleanup(() => viewport?.dispose());
      controller.registerCleanup(() => clearOverlays());
    },

    render(state: ApplicationsState, ctx): void {
      const preset = getPreset(state);
      const { param, mode } = state;
      const domain = preset.domain;
      const chartEnabled = mode === 'optimization' && preset.source !== undefined;
      setChartMode(preset, chartEnabled);

      // Ensure overlay groups exist and are in the SVG.
      const svg = curves.group.ownerSVGElement!;
      if (!arrowGroup) {
        arrowGroup = createSvgEl('g', { class: 'calc-arrows' });
        svg.appendChild(arrowGroup);
      }
      // ── Clear stale overlay content ──
      while (arrowGroup.firstChild) {
        arrowGroup.removeChild(arrowGroup.firstChild);
      }

      if (chartEnabled && preset.source) {
        // ──────────────────────────────────────────────────────────────────────
        // OPTIMIZATION MODE
        // ──────────────────────────────────────────────────────────────────────
        const quantityFn = compileFn(preset.source);
        const visibleDomains = quantityPlane.getDomains();

        // Draw quantity curve in the lower pane via the quantity projector.
        const quantityPts = sampleFn(
          quantityFn,
          visibleDomains.xDomain[0],
          visibleDomains.xDomain[1],
          SAMPLE_N,
        );
        quantityCurves.setPolyline('quantity', quantityPts, 'calc-curve-primary');

        // Clamp param to domain
        const clampedParam = clamp(param, domain[0], domain[1]);

        // Marker on the quantity curve at current param.
        const markerY = quantityFn(clampedParam);
        const markerScreen = quantityPlane.worldToScreen({ x: clampedParam, y: markerY });

        // Compute tangent direction at current param using the symbolic derivative.
        const fPrimeNode = simplify(derivative(parse(preset.source).node, 'x'));
        const fPrimeFn = compileFn(stringify(fPrimeNode));
        const slope = fPrimeFn(clampedParam);
        const slopeMag = Math.abs(slope);
        const isAtOptimum = slopeMag < 0.05;

        // Tangent segment at marker: ±0.5 domain units.
        // These are world-space points passed to quantityCurves which will
        // project them through the quantity projector.
        const tHalf = (visibleDomains.xDomain[1] - visibleDomains.xDomain[0]) * 0.05;
        const x0t = clampedParam - tHalf;
        const x1t = clampedParam + tHalf;
        const y0t = markerY + slope * (x0t - clampedParam);
        const y1t = markerY + slope * (x1t - clampedParam);
        quantityCurves.setPolyline(
          'marker-tangent',
          [
            { x: x0t, y: y0t },
            { x: x1t, y: y1t },
          ],
          'calc-curve-tangent',
        );

        // Build scenario figure elements based on preset id.
        let sceneElements: FigureElement[];
        if (preset.id === 'open-box') {
          sceneElements = buildOpenBoxElements(clampedParam, grid);
        } else {
          // rect-under-line
          sceneElements = buildRectElements(clampedParam, grid);
        }

        // Add the quantity-pane marker as an additional figure point.
        if (isPointInCartesianDomain({ x: clampedParam, y: markerY }, visibleDomains)) {
          sceneElements.push({
            kind: 'point',
            id: 'marker',
            at: markerScreen,
            style: { className: isAtOptimum ? 'geo-point-draggable' : 'geo-point-main' },
          });
        }

        // Vertical guide from marker up through both panes.
        const guideTop: Vec2 = { x: markerScreen.x, y: 0 };
        const guideMid: Vec2 = { x: markerScreen.x, y: QUANTITY_TOP };
        sceneElements.push(
          {
            kind: 'point',
            id: 'guide-top',
            at: guideTop,
            style: { className: 'geo-point-off-stage' },
          },
          {
            kind: 'point',
            id: 'guide-mid',
            at: guideMid,
            style: { className: 'geo-point-off-stage' },
          },
          {
            kind: 'segment',
            id: 'guide-vert',
            from: 'guide-top',
            to: 'guide-mid',
            style: { className: 'geo-line geo-line--faint', dashed: true },
          },
        );

        figure.update(sceneElements);
        curves.group.ownerSVGElement
          ?.querySelector('[data-figure-id="pt-param"]')
          ?.setAttribute('data-no-pan', 'true');
        legend.setEntries([
          { id: 'quantity', formula: `Q(x) = ${preset.source}`, color: 'var(--accent)' },
          {
            id: 'tangent',
            formula: tangentFormula(clampedParam, markerY, slope),
            color: 'var(--red)',
            lineStyle: 'dashed',
          },
        ]);

        // Readout — use memoised optimum (recompute only on preset change)
        if (cachedOptimumPresetId !== preset.id) {
          cachedOptimum = optimumOf(preset.source, domain);
          cachedOptimumPresetId = preset.id;
        }
        const optimum = cachedOptimum!;
        const rows: ReadoutRow[] = [
          {
            id: 'quantity',
            lhs: 'Q(x)',
            relation: '=' as const,
            rhs: formatNumber(markerY, { decimals: 3 }),
          },
          {
            id: 'f-prime',
            lhs: "Q'(x)",
            relation: '=' as const,
            rhs: formatNumber(slope, { decimals: 4 }),
            emphasis: isAtOptimum ? ('invariant' as const) : ('neutral' as const),
          },
          {
            id: 'optimum-x',
            lhs: 'optimum x',
            relation: '≈' as const,
            rhs: formatNumber(optimum.x, { decimals: 3 }),
            emphasis: 'neutral' as const,
          },
          {
            id: 'optimum-kind',
            lhs: 'kind',
            relation: '=' as const,
            rhs: optimum.kind,
            emphasis: 'neutral' as const,
          },
        ];

        if (ctx.advanced) {
          rows.push({
            id: 'source',
            lhs: 'Q(x) =',
            relation: '=' as const,
            rhs: preset.source,
            emphasis: 'neutral' as const,
          });
        }

        readout.update(rows);
      } else if (mode === 'related-rates') {
        // ──────────────────────────────────────────────────────────────────────
        // RELATED-RATES MODE
        // ──────────────────────────────────────────────────────────────────────

        // Clear quantity pane artifacts
        quantityCurves.clear('quantity');
        quantityCurves.clear('marker-tangent');

        let sceneElements: FigureElement[];
        let rrResult: RelatedRatesResult;

        if (preset.scenario === 'ladder') {
          sceneElements = buildLadderElements(param, grid, svg, arrowGroup);
          rrResult = relatedRates('ladder', { x: param, length: 10, dxdt: 1 });
        } else {
          // ripple
          sceneElements = buildRippleElements(param, grid, svg, arrowGroup);
          rrResult = relatedRates('ripple', { r: param, drdt: 2 });
        }

        figure.update(sceneElements);
        curves.group.ownerSVGElement
          ?.querySelector('[data-figure-id="pt-param"]')
          ?.setAttribute('data-no-pan', 'true');

        // Readout
        const rows: ReadoutRow[] = [
          {
            id: 'equation',
            lhs: 'chain rule',
            relation: '=' as const,
            rhs: rrResult.equation,
            emphasis: 'invariant' as const,
          },
        ];

        if (preset.scenario === 'ladder') {
          const { dxdt, dydt } = rrResult.vectors;
          rows.push(
            {
              id: 'dx-dt',
              lhs: 'dx/dt',
              relation: '=' as const,
              rhs: formatNumber(dxdt!, { decimals: 2 }),
            },
            {
              id: 'dy-dt',
              lhs: 'dy/dt',
              relation: '=' as const,
              rhs: formatNumber(dydt!, { decimals: 4 }),
            },
          );
        } else {
          const { drdt, dAdt } = rrResult.vectors;
          rows.push(
            {
              id: 'dr-dt',
              lhs: 'dr/dt',
              relation: '=' as const,
              rhs: formatNumber(drdt!, { decimals: 2 }),
            },
            {
              id: 'dA-dt',
              lhs: 'dA/dt',
              relation: '=' as const,
              rhs: formatNumber(dAdt!, { decimals: 4 }),
            },
          );
        }

        if (ctx.advanced) {
          rows.push({
            id: 'param',
            lhs: preset.scenario === 'ladder' ? 'x (base)' : 'r (radius)',
            relation: '=' as const,
            rhs: formatNumber(param, { decimals: 3 }),
          });
        }

        readout.update(rows);
      } else {
        // Fallback: clear curves and render empty state.
        quantityCurves.clear('quantity');
        quantityCurves.clear('marker-tangent');
        figure.update([]);
        readout.update([]);
      }
    },

    onDrag(state: ApplicationsState, pointId: string, at: Vec2): ApplicationsState {
      if (pointId === 'pt-param') {
        const preset = getPreset(state);
        const domain = preset.domain;

        let newParam: number;
        if (state.mode === 'optimization') {
          // For the optimization pane, the draggable is positioned via the
          // grid worldToScreen mapping; invert to get the world x then math x.
          const worldPt = grid.screenToWorld(at);
          // World x → math x: MATH_ORIGIN_X = STAGE_W / 2 / UNIT
          const MATH_OX = STAGE_W / 2 / UNIT;
          const mathX = worldPt.x - MATH_OX;
          newParam = clamp(mathX, domain[0], domain[1]);
        } else if (preset.scenario === 'ladder') {
          // Ladder: draggable is at (wallBaseX + x * scale, wallBaseY).
          // Invert to recover x. Scale constants must match buildLadderElements.
          const MARGIN_LEFT = 80;
          const MARGIN_RIGHT = 80;
          const MARGIN_TOP = 16;
          const MARGIN_BOTTOM = 24;
          const ladderLength = 10;
          const usableW = STAGE_W - MARGIN_LEFT - MARGIN_RIGHT;
          const usableH = SCENARIO_H - MARGIN_TOP - MARGIN_BOTTOM;
          const ladderScale = Math.min(usableW / ladderLength, usableH / ladderLength);
          const rawParam = (at.x - MARGIN_LEFT) / ladderScale;
          newParam = clamp(rawParam, domain[0], domain[1]);
        } else {
          // Ripple: draggable is at the right edge of the circle.
          // circleEdgeRight.x = cx + rScreen, where rScreen = (r / 8) * maxR,
          // cx = STAGE_W / 2, maxR = Math.min(cx - 60, SCENARIO_H / 2 - 20).
          const cx = STAGE_W / 2;
          const cy = SCENARIO_H / 2;
          const maxR = Math.min(cx - 60, cy - 20);
          const rScreen = at.x - cx;
          const rawParam = (rScreen / maxR) * 8;
          newParam = clamp(rawParam, domain[0], domain[1]);
        }

        return { ...state, param: newParam };
      }
      return state;
    },

    panel(state: ApplicationsState, ctx): PanelRow<CalcKey, ApplicationsState>[] {
      const rows: PanelRow<CalcKey, ApplicationsState>[] = [];
      const preset = getPreset(state);
      const domain = preset.domain;

      // Filter presets by current mode (advanced shows all; basic shows only optimization).
      // Always include the currently-active preset so the dropdown value always matches
      // an option — e.g. if a related-rates preset is active in non-advanced mode.
      const basePresets = ctx.advanced
        ? APPLICATION_PRESETS
        : APPLICATION_PRESETS.filter((p) => p.kind === 'optimization');
      const visiblePresets = basePresets.some((p) => p.id === state.presetId)
        ? basePresets
        : [preset, ...basePresets];

      rows.push({
        kind: 'dropdown',
        id: 'applications-preset',
        labelKey: 'applications.panel.preset',
        options: visiblePresets.map((p) => ({ value: p.id, labelKey: p.labelKey })),
        value: state.presetId,
        onChange: (v) => applyPresetLocal(state, v),
      });

      rows.push({
        kind: 'slider',
        id: 'applications-param',
        labelKey: 'applications.panel.param',
        min: domain[0],
        max: domain[1],
        step: (domain[1] - domain[0]) / 100,
        value: state.param,
        format: (v) => v.toFixed(2),
        onChange: (v) => ({ ...state, param: v }),
      });

      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      if (ctx.advanced) {
        rows.push({
          kind: 'dropdown',
          id: 'applications-mode',
          labelKey: 'applications.panel.mode',
          options: [
            { value: 'optimization', labelKey: 'applications.mode.optimization' },
            { value: 'related-rates', labelKey: 'applications.mode.related-rates' },
          ],
          value: state.mode,
          onChange: (v) => ({ ...state, mode: v as ApplicationsState['mode'] }),
        });
      }

      return rows;
    },

    actions: (_state: ApplicationsState, ctx): ActionBarSpec<CalcKey, ApplicationsState> => {
      return {
        onReset: () => defaultState(ctx),
        onRandomise: (s) => {
          const pool = APPLICATION_PRESETS.filter((p) => ctx.advanced || p.kind === 'optimization');
          const idx = Math.floor(ctx.prng() * pool.length);
          const preset = pool[idx] ?? pool[0]!;
          const [x0, x1] = preset.domain;
          return {
            ...s,
            presetId: preset.id,
            param: (x0 + x1) / 3,
            mode: preset.kind,
          };
        },
        presets: APPLICATION_PRESETS.map((p) => ({ id: p.id, labelKey: p.labelKey })),
        onPreset: (s, id) => applyPresetLocal(s, id),
        viewReset: { labelKey: 'action.reset-view', onResetView: () => viewport?.reset() },
      };
    },

    applyShotState(state: ApplicationsState, _shotId: string): ApplicationsState {
      const preset = getPreset(state);
      if (state.mode === 'optimization' && preset.source) {
        // Pin param to the optimum
        const optimum = optimumOf(preset.source, preset.domain);
        return { ...state, param: optimum.x };
      }
      // Related-rates: use mid-domain for a clear scenario
      const [x0, x1] = preset.domain;
      return { ...state, param: (x0 + x1) / 2 };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
